/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useAsyncDebounce } from "react-table";
import moment from "moment";
import { api } from "core/api";
import { Card, Input, SvgIcon, ControlledTable } from "core/components";
import { admin_material_requests_path, admin_material_request_path } from "routes";
import { navigateTo } from "core/navigation";
import ControlledTableContext from "core/components/ControlledTableContext";

type MaterialRequestHistoryProps = {
  hasMaterialRequestHistory: boolean;
  hasWarehousesAccess: boolean;
};

const MaterialRequestHistory = ({ hasMaterialRequestHistory, hasWarehousesAccess }: MaterialRequestHistoryProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [materialRequests, setMaterialRequests] = React.useState([]);
  const [filterDate, setFilterDate] = React.useState(null);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "code",
        Header: "Material Request",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "date_needed",
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        id: "project_code",
        Header: "Project",
        accessor: "project.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Approved By",
        accessor: "approved_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Released By",
        accessor: "released_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Items",
        accessor: "total_items",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 5,
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.material_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Material Request"
                data-bs-toggle="tooltip"
                onClick={() => {
                  navigateTo(admin_material_request_path(props.row.original.id));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
            </div>
          );
        }
      }
    ],
    []
  );

  const getMaterialRequests = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(admin_material_requests_path({ history: true, page, per, filters: JSON.stringify(filters) }), { signal });
      setMaterialRequests(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material request history.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Material Request Number" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <DatePicker
            selected={filterDate}
            onChange={(date) => {
              if (date) {
                const d = new Date(date);
                d.setDate(d.getDate() + 1);
                d.setUTCHours(0, 0, 0, 0);
                table.current.setFilter("date_needed", d.toISOString());
                setFilterDate(date);
              } else {
                table.current.setFilter("date_needed", "");
                setFilterDate(null);
              }
            }}
            isClearable
            className="field"
            placeholderText="Select Date Needed"
          />
        </Col>
        <Col>
          <Input placeholder="Search for Project" onChange={(e) => onChange("project_code", e.target.value)} />
        </Col>
        {hasWarehousesAccess && (
          <Col>
            <Input placeholder="Search for Warehouse" onChange={(e) => onChange("warehouse", e.target.value)} />
          </Col>
        )}
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={materialRequests}
              ref={table}
              isLoading={isLoading}
              fetchData={getMaterialRequests}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return hasMaterialRequestHistory ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Card.Body>{content()}</Card.Body>
    </React.StrictMode>
  );
};

export default MaterialRequestHistory;
